import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import TransactionItem, { isDailyFirstItem } from './TransactionItem';
import * as S from './styled';

const TransactionsList = ({
  transactions,
  workspaces,
  listItems,
  dayPoints,
  transactionsCount,
  loadMoreTransactions,
  infiniteLoaderRef,
  autosizerRef,
  listRef,
  isItemLoaded,
}) => {
  const ownListRef = useRef();
  const [expandState] = useState(() => new Map());

  useEffect(() => {
    expandState.clear();
  }, [transactions]);

  const getItemSize = (index) => {
    const isShowTimeline = isDailyFirstItem({
      index,
      transactions,
      listItems,
    });

    let height = 80 + (isShowTimeline ? 20 : 0);

    if (expandState.has(index)) {
      height += expandState.get(index);
    }

    return height;
  };

  const onClickExpandTransaction = (index) => {
    if (expandState.has(index)) {
      expandState.delete(index);
      ownListRef.current.resetAfterIndex(index, true);
      return false;
    }
    expandState.set(index, null);
    ownListRef.current.resetAfterIndex(index, true);
    return true;
  };

  const isTransactionDetailsExpanded = (index) => {
    return expandState.has(index);
  };

  const onResizeDetailsArea = (index, size) => {
    if (expandState.has(index)) {
      // Ignore resize event when ListItem is out of viewport
      if (size.height !== 0 && expandState.get(index) !== size.height) {
        expandState.set(index, size.height);
        ownListRef.current.resetAfterIndex(index, true);
      }
    }
  };

  return (
    <S.TransactionsList>
      <AutoSizer ref={autosizerRef}>
        {({ height, width }) => {
          return (
            <InfiniteLoader
              ref={infiniteLoaderRef}
              isItemLoaded={isItemLoaded}
              itemCount={transactionsCount}
              loadMoreItems={loadMoreTransactions}
              minimumBatchSize={10}
            >
              {({ onItemsRendered, ref }) => (
                <S.List
                  height={height}
                  width={width}
                  itemCount={transactionsCount}
                  itemSize={getItemSize}
                  overscanCount={15}
                  onItemsRendered={onItemsRendered}
                  ref={(el) => {
                    if (listRef) {
                      listRef.current = el;
                    }
                    ownListRef.current = el;
                    ref(el);
                  }}
                  itemKey={(index, data) => {
                    const item = data[index];
                    return item?.id || index;
                  }}
                  itemData={{
                    transactions,
                    workspaces,
                    listItems,
                    dayPoints,
                    isItemLoaded,
                    isTransactionDetailsExpanded,
                    onResizeDetailsArea,
                    onClickExpandTransaction,
                  }}
                >
                  {TransactionItem}
                </S.List>
              )}
            </InfiniteLoader>
          );
        }}
      </AutoSizer>
    </S.TransactionsList>
  );
};

TransactionsList.propTypes = {
  transactions: PropTypes.object.isRequired,
  workspaces: PropTypes.object.isRequired,
  listItems: PropTypes.object.isRequired,
  dayPoints: PropTypes.object.isRequired,
  transactionsCount: PropTypes.number.isRequired,
  loadMoreTransactions: PropTypes.func.isRequired,
  infiniteLoaderRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(InfiniteLoader) }),
  ]).isRequired,
  listRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.oneOf([null]).isRequired,
      PropTypes.instanceOf(VariableSizeList),
    ]),
  }),
  autosizerRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.oneOf([null]).isRequired,
      PropTypes.instanceOf(AutoSizer),
    ]),
  }),
  isItemLoaded: PropTypes.func.isRequired,
};

TransactionsList.defaultProps = {};

export default TransactionsList;
